import { useEffect, useRef, useState } from "react";
import { MapPin, Calendar, Users, Loader2, Plus, Minus, Search } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";

/**
 * MakeMyTrip / Goibibo-style hotel search bar.
 * Single horizontal row on desktop, stacked on mobile.
 *  [City] [Check-in] [Check-out] [Rooms & Guests ▾] [Check Availability]
 *
 * Props:
 *  - onSubmit(payload, { setSubmitting }) — invoked with the normalised payload
 *    when the user clicks "Check Availability". Parent decides where to POST.
 *  - initial — optional defaults
 */
export default function HotelSearchBar({ onSubmit, initial = {} }) {
  const [city, setCity] = useState(initial.city || "");
  const [checkIn, setCheckIn] = useState(initial.check_in || "");
  const [checkOut, setCheckOut] = useState(initial.check_out || "");
  const [rooms, setRooms] = useState(initial.rooms || 1);
  const [adults, setAdults] = useState(initial.pax_adults || 2);
  const [children, setChildren] = useState(initial.pax_children || 0);
  const [childrenAges, setChildrenAges] = useState(initial.children_ages || []);
  const [guestsOpen, setGuestsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const popoverRef = useRef(null);

  // Keep childrenAges array length in sync with `children` counter
  useEffect(() => {
    setChildrenAges(prev => {
      const next = [...prev];
      while (next.length < children) next.push(0);
      next.length = children;
      return next;
    });
  }, [children]);

  // Close popover on outside-click
  useEffect(() => {
    const onClick = (e) => {
      if (guestsOpen && popoverRef.current && !popoverRef.current.contains(e.target)) {
        setGuestsOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [guestsOpen]);

  const nights = (() => {
    if (!checkIn || !checkOut) return 0;
    const ms = new Date(checkOut) - new Date(checkIn);
    if (isNaN(ms)) return 0;
    return Math.round(ms / 86400000);
  })();

  const guestsSummary = (() => {
    const parts = [`${rooms} Room${rooms === 1 ? "" : "s"}`, `${adults} Adult${adults === 1 ? "" : "s"}`];
    if (children > 0) parts.push(`${children} Child${children === 1 ? "" : "ren"}`);
    return parts.join(", ");
  })();

  const handleSubmit = async () => {
    if (!city.trim()) { toast.error("Please enter a city / area / property name"); return; }
    if (!checkIn || !checkOut) { toast.error("Please pick check-in and check-out dates"); return; }
    if (nights <= 0) { toast.error("Check-out must be after check-in"); return; }
    if (children > 0 && childrenAges.some(a => a === null || a === undefined || a === "")) {
      toast.error("Please pick an age for every child");
      return;
    }
    // Derive CWB / CNB from age: age >= 5 → CWB, else CNB (industry standard cutoff).
    // Admin can still override at the availability step.
    const cwb = childrenAges.filter(a => Number(a) >= 5).length;
    const cnb = children - cwb;
    const payload = {
      hotel_name: "",
      city: city.trim(),
      destination: "",
      check_in: checkIn,
      check_out: checkOut,
      rooms: parseInt(rooms, 10),
      pax_adults: parseInt(adults, 10),
      pax_children: parseInt(children, 10),
      children_ages: childrenAges.map(a => parseInt(a, 10) || 0),
      cwb,
      cnb,
      meal_plan: "CP",
      special_requests: "",
    };
    try {
      setSubmitting(true);
      await onSubmit(payload, { setSubmitting });
    } finally {
      setSubmitting(false);
    }
  };

  const Counter = ({ value, onChange, min = 0, max = 30, testid }) => (
    <div className="flex items-center gap-2">
      <button type="button" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min}
        className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        data-testid={`${testid}-dec`}>
        <Minus className="w-3.5 h-3.5" />
      </button>
      <span className="w-6 text-center font-semibold tabular-nums" data-testid={`${testid}-val`}>{value}</span>
      <button type="button" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max}
        className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        data-testid={`${testid}-inc`}>
        <Plus className="w-3.5 h-3.5" />
      </button>
    </div>
  );

  return (
    <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-3 sm:p-4" data-testid="hotel-search-bar">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3">
        {/* City */}
        <div className="md:col-span-4 rounded-xl border border-gray-200 hover:border-emerald-300 transition-colors px-3 py-2.5 cursor-text">
          <Label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1"><MapPin className="w-3 h-3" /> City / Area / Property</Label>
          <Input value={city} onChange={e => setCity(e.target.value)} placeholder="e.g. Shillong"
            className="border-0 bg-transparent px-0 h-7 text-base font-bold focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-gray-400 placeholder:font-normal"
            data-testid="search-city" />
          <p className="text-xs text-gray-500">India</p>
        </div>

        {/* Check-In */}
        <div className="md:col-span-2 rounded-xl border border-gray-200 hover:border-emerald-300 transition-colors px-3 py-2.5">
          <Label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1"><Calendar className="w-3 h-3" /> Check-In</Label>
          <Input type="date" value={checkIn} onChange={e => setCheckIn(e.target.value)}
            className="border-0 bg-transparent px-0 h-7 text-sm font-semibold focus-visible:ring-0 focus-visible:ring-offset-0"
            data-testid="search-checkin" />
        </div>

        {/* Check-Out */}
        <div className="md:col-span-2 rounded-xl border border-gray-200 hover:border-emerald-300 transition-colors px-3 py-2.5">
          <Label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1"><Calendar className="w-3 h-3" /> Check-Out</Label>
          <Input type="date" value={checkOut} onChange={e => setCheckOut(e.target.value)} min={checkIn || undefined}
            className="border-0 bg-transparent px-0 h-7 text-sm font-semibold focus-visible:ring-0 focus-visible:ring-offset-0"
            data-testid="search-checkout" />
        </div>

        {/* Rooms & Guests popover */}
        <div className="md:col-span-2 relative" ref={popoverRef}>
          <button type="button" onClick={() => setGuestsOpen(o => !o)}
            className={`w-full text-left rounded-xl border ${guestsOpen ? "border-emerald-400 ring-2 ring-emerald-100" : "border-gray-200 hover:border-emerald-300"} transition-colors px-3 py-2.5`}
            data-testid="search-guests-toggle">
            <Label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1 cursor-pointer"><Users className="w-3 h-3" /> Rooms &amp; Guests</Label>
            <p className="h-7 text-sm font-semibold text-gray-900 leading-7 truncate">{guestsSummary}</p>
          </button>
          {guestsOpen && (
            <div className="absolute z-50 mt-2 right-0 w-80 bg-white rounded-xl shadow-xl border border-gray-200 p-4 space-y-4" data-testid="search-guests-popover">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-gray-900">Room</p>
                <Counter value={rooms} onChange={setRooms} min={1} max={15} testid="search-rooms" />
              </div>
              <div className="flex items-center justify-between">
                <p className="font-semibold text-gray-900">Adults</p>
                <Counter value={adults} onChange={setAdults} min={1} max={30} testid="search-adults" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-900">Children</p>
                  <p className="text-[11px] text-gray-500">0 - 17 Years Old</p>
                </div>
                <Counter value={children} onChange={setChildren} min={0} max={15} testid="search-children" />
              </div>
              {children > 0 && (
                <>
                  <p className="text-[11px] text-gray-500 -mb-1">Please provide right age for best options and prices.</p>
                  <div className="pt-2 border-t border-gray-100">
                    <p className="text-sm font-semibold text-gray-900 mb-2">Age of Children</p>
                    <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto pr-1">
                      {Array.from({ length: children }).map((_, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-xs text-gray-600 whitespace-nowrap">Child {i + 1}</span>
                          <select value={childrenAges[i] ?? ""} onChange={e => {
                            const v = e.target.value === "" ? "" : parseInt(e.target.value, 10);
                            setChildrenAges(prev => { const n = [...prev]; n[i] = v; return n; });
                          }}
                            className="flex-1 h-8 text-xs rounded-md border border-gray-200 bg-gray-50 px-2"
                            data-testid={`search-child-age-${i}`}>
                            <option value="">Select</option>
                            {Array.from({ length: 18 }).map((_, age) => (<option key={age} value={age}>{age} {age === 1 ? "year" : "years"}</option>))}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
              <Button onClick={() => setGuestsOpen(false)} className="w-full bg-emerald-600 hover:bg-emerald-700">Apply</Button>
            </div>
          )}
        </div>

        {/* Submit */}
        <div className="md:col-span-2 flex">
          <Button onClick={handleSubmit} disabled={submitting}
            className="w-full h-full min-h-[60px] rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm gap-2"
            data-testid="search-submit">
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Search className="w-5 h-5" /> Check Availability</>}
          </Button>
        </div>
      </div>

      {/* Nights preview strip */}
      {nights > 0 && (
        <div className="mt-3 px-3 py-1.5 inline-flex items-center gap-2 text-xs rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800" data-testid="search-nights-strip">
          <Calendar className="w-3 h-3" /> <span className="font-semibold">{nights}N</span> · {nights + 1} day{nights ? "s" : ""}
        </div>
      )}
    </div>
  );
}
